import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismMetricBlock } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import changeColorClass from 'lumen/helpers/change-color-class';
import type { TotalRevenueBreakdownTrendItem } from 'lumen/models';
import {
  METRIC_PAR_TRANSLATIONS,
  METRIC_TRANSLATIONS,
} from 'lumen/utils/constants/total-revenue-constants';

interface MarketTotalRevenueContributionBreakdownTooltipSignature {
  Args: {
    item: TotalRevenueBreakdownTrendItem;
    referenceType: string;
  };
}

export default class MarketTotalRevenueContributionBreakdownTooltip extends Component<MarketTotalRevenueContributionBreakdownTooltipSignature> {
  @service declare dates: Services['dates'];
  @service declare totalRevenue: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get metricTranslationKey() {
    const translationMappingToUse = this.totalRevenue.isPerAvailableRoom
      ? METRIC_PAR_TRANSLATIONS
      : METRIC_TRANSLATIONS;

    return translationMappingToUse.totalNonRoomRevenueOrPAR;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referenceItem() {
    return this.args.item.referenceValues.find(
      (reference) => reference.referenceType === this.args.referenceType,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tooltipTitle() {
    const format = this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG;
    return this.args.item.date.format(format);
  }

  <template>
    <div
      class="rounded-100 border-neutral-default bg-elevation-surface border border-solid shadow-neutral-200"
    >
      <div
        class="text-300--regular border-neutral-default border-b border-solid pt-200 pr-300 pb-200 pl-300 text-center"
      >
        {{this.tooltipTitle}}
      </div>

      <div class="flex flex-row justify-evenly gap-1000 pr-300 pl-300">
        <PrismMetricBlock>
          <:value>{{@item.formattedValue}}</:value>
          <:label>{{t this.metricTranslationKey}}</:label>
        </PrismMetricBlock>

        <PrismMetricBlock>
          <:value>
            {{this.referenceItem.formattedValue}}
            <span
              class="text-200--regular
                {{changeColorClass (or this.referenceItem.relativeChange 0)}}"
            >
              ({{this.referenceItem.formattedRelativeChange}})
            </span>
          </:value>
          <:label>{{t
              "general.on.period"
              period=this.referenceItem.translatedReferenceType
            }}</:label>
        </PrismMetricBlock>
      </div>
    </div>
  </template>
}
